import { useState } from "react";
import type { ParticipantStance, TrialLogEntry } from "../types/study";

type BlockStats = {
  blockIndex: number;
  blockType: "meta" | "non_meta";
  n: number;
  meanAttitude: number;
  meanLogic: number;
  meanFor: number | null;
  meanRtS: number;
};

type ActiveSelection =
  | { type: "block"; index: number }
  | { type: "group"; kind: "meta" | "non_meta" }
  | null;

function avg(arr: number[]): number {
  return arr.length === 0 ? 0 : arr.reduce((s, v) => s + v, 0) / arr.length;
}

function intensityLabel(v: number): string {
  if (v < 0.3) return "中立";
  if (v < 0.8) return "较弱";
  if (v < 1.5) return "中等";
  return "较强";
}

function stanceLabel(s: ParticipantStance): string {
  return s === "feminist_leaning" ? "女权支持倾向" : "保守 / 怀疑倾向";
}

function blockTypeLabel(t: "meta" | "non_meta"): string {
  return t === "meta" ? "元认知" : "非元认知";
}

export function ResultsPanel({
  trialLog,
  stance,
  intensity,
}: {
  trialLog: TrialLogEntry[];
  stance: ParticipantStance;
  intensity: number;
}) {
  const [active, setActive] = useState<ActiveSelection>(null);

  const famTrials = trialLog.filter(
    (t) => t.itemRole === "key" && t.stanceVersion === stance
  );
  if (famTrials.length === 0) return null;

  const byBlock = new Map<number, TrialLogEntry[]>();
  for (const t of famTrials) {
    const list = byBlock.get(t.blockIndex) ?? [];
    list.push(t);
    byBlock.set(t.blockIndex, list);
  }

  const blockStats: BlockStats[] = Array.from(byBlock.entries())
    .sort(([a], [b]) => a - b)
    .map(([idx, trials]) => {
      const forVals = trials.filter((t) => t.forScore !== null).map((t) => t.forScore!);
      return {
        blockIndex: idx,
        blockType: trials[0].blockType,
        n: trials.length,
        meanAttitude: avg(trials.map((t) => t.attitudeScore)),
        meanLogic: avg(trials.map((t) => t.logicScore)),
        meanFor: forVals.length > 0 ? avg(forVals) : null,
        meanRtS: avg(trials.map((t) => t.rtMs)) / 1000,
      };
    });

  const nonMeta = blockStats.filter((b) => b.blockType === "non_meta");
  const meta = blockStats.filter((b) => b.blockType === "meta");
  // Display columns grouped by type so tree header brackets align correctly
  const displayBlocks = [...nonMeta, ...meta];
  const intensityPct = Math.min(100, (intensity / 3) * 100);

  const rtValues = displayBlocks.map((b) => b.meanRtS);
  const minRt = Math.min(...rtValues);
  const maxRt = Math.max(...rtValues);
  const rtRange = maxRt - minRt || 1;

  const yTicks = [5, 4, 3, 2, 1];

  function toggle(next: ActiveSelection) {
    setActive((prev) => {
      if (!prev || !next) return next;
      if (prev.type !== next.type) return next;
      if (prev.type === "block" && next.type === "block" && prev.index === next.index) return null;
      if (prev.type === "group" && next.type === "group" && prev.kind === next.kind) return null;
      return next;
    });
  }

  function isColActive(b: BlockStats) {
    return active?.type === "block" && active.index === b.blockIndex;
  }
  function isColGroupActive(b: BlockStats) {
    return active?.type === "group" && active.kind === b.blockType;
  }
  function isColDimmed(b: BlockStats) {
    return active !== null && !isColActive(b) && !isColGroupActive(b);
  }

  // Detail panel data
  const detailBlock =
    active?.type === "block"
      ? blockStats.find((b) => b.blockIndex === active.index) ?? null
      : null;
  const detailGroup =
    active?.type === "group"
      ? blockStats.filter((b) => b.blockType === active.kind)
      : null;

  return (
    <div className="rp-shell">
      <div className="rp-panel-header">
        <span className="rp-panel-title">你的实验数据</span>
        <span className="rp-panel-badge">仅含同立场谬误（FAM）试次</span>
      </div>

      {/* Stance bar */}
      <div className="rp-stance">
        <div className="rp-stance-left">
          <span className="rp-stance-tag">{stanceLabel(stance)}</span>
          <span className="rp-stance-level">{intensityLabel(intensity)}</span>
        </div>
        <div className="rp-stance-bar-wrap">
          <div className="rp-stance-track">
            <div className="rp-stance-fill" style={{ width: `${intensityPct}%` }} />
          </div>
        </div>
      </div>

      {/* Chart area */}
      <div className="rp-chart-wrap">

        {/* Tree header with clickable group labels */}
        <div className="rp-tree-row">
          <div className="rp-y-spacer" />
          <div className="rp-branches">
            {nonMeta.length > 0 && (
              <button
                type="button"
                className={`rp-branch rp-branch-nonmeta${active?.type === "group" && active.kind === "non_meta" ? " is-active" : ""}`}
                style={{ flex: nonMeta.length }}
                onClick={() => toggle({ type: "group", kind: "non_meta" })}
              >
                <span className="rp-branch-label">非元认知</span>
              </button>
            )}
            {meta.length > 0 && (
              <button
                type="button"
                className={`rp-branch rp-branch-meta${active?.type === "group" && active.kind === "meta" ? " is-active" : ""}`}
                style={{ flex: meta.length }}
                onClick={() => toggle({ type: "group", kind: "meta" })}
              >
                <span className="rp-branch-label">元认知</span>
              </button>
            )}
          </div>
        </div>

        {/* Main chart: Y-axis + bars */}
        <div className="rp-chart">
          <div className="rp-y-axis">
            {yTicks.map((v) => <span key={v}>{v}</span>)}
          </div>
          <div className="rp-chart-body">
            {yTicks.map((v) => (
              <div key={v} className="rp-gridline" style={{ bottom: `${(v / 5) * 100}%` }} />
            ))}
            <div className="rp-cols">
              {displayBlocks.map((b, i) => {
                const isFirstMeta = i === nonMeta.length;
                return (
                  <button
                    key={b.blockIndex}
                    type="button"
                    className={[
                      "rp-col",
                      isFirstMeta ? "rp-col-group-start" : "",
                      isColActive(b) ? "is-active" : "",
                      isColGroupActive(b) ? "is-group-active" : "",
                      isColDimmed(b) ? "is-dimmed" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => toggle({ type: "block", index: b.blockIndex })}
                    title={`Section ${b.blockIndex + 1} · ${blockTypeLabel(b.blockType)}`}
                  >
                    <div className="rp-bar-group">
                      <VBar value={b.meanAttitude} fillClass="rp-vfill-att" label="认同" />
                      <VBar value={b.meanLogic} fillClass="rp-vfill-log" label="逻辑" />
                      {b.meanFor !== null && (
                        <VBar value={b.meanFor} fillClass="rp-vfill-for" label="FOR" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* RT strip */}
        <div className="rp-rt-strip">
          <div className="rp-y-spacer rp-rt-axis-label">RT</div>
          <div className="rp-rt-cells">
            {displayBlocks.map((b) => {
              const pct = ((b.meanRtS - minRt) / rtRange) * 70 + 20; // 20–90%
              return (
                <div
                  key={b.blockIndex}
                  className={`rp-rt-cell${isColDimmed(b) ? " is-dimmed" : ""}${isColActive(b) ? " is-active" : ""}`}
                >
                  <div className="rp-rt-bar-track">
                    <div className="rp-rt-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="rp-rt-num">{b.meanRtS.toFixed(1)}s</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section labels below */}
        <div className="rp-col-labels">
          <div className="rp-y-spacer" />
          <div className="rp-label-row">
            {displayBlocks.map((b) => (
              <div key={b.blockIndex} className={`rp-col-label-cell${isColDimmed(b) ? " is-dimmed" : ""}`}>
                <span className="rp-sec-name">S{b.blockIndex + 1}</span>
                <span className="rp-sec-n">n={b.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {active && (
        <div className="rp-detail">
          {detailBlock && <BlockDetail stats={detailBlock} />}
          {detailGroup && active.type === "group" && (
            <GroupDetail blocks={detailGroup} kind={active.kind} />
          )}
        </div>
      )}

      {!active && (
        <p className="rp-hint">点击组标签或柱状区域查看详情</p>
      )}

      {/* Legend */}
      <div className="rp-legend">
        <div className="rp-legend-row">
          <span className="rp-legend-swatch rp-vfill-att" />
          <span><strong>认同</strong> 情感接受度（1–5）</span>
        </div>
        <div className="rp-legend-row">
          <span className="rp-legend-swatch rp-vfill-log" />
          <span><strong>逻辑</strong> 论证合理度（1–5）</span>
        </div>
        <div className="rp-legend-row">
          <span className="rp-legend-swatch rp-vfill-for" />
          <span><strong>FOR</strong> 元认知确信度——「回顾刚才的判断，我感觉是正确的」（1–5，仅元认知组）</span>
        </div>
        <div className="rp-legend-row">
          <span className="rp-legend-swatch rp-swatch-rt" />
          <span><strong>RT</strong> 平均反应时（秒），越短越直觉主导</span>
        </div>
        <div className="rp-legend-row">
          <span><strong>FAM 试次</strong> 仅统计与你立场同向的谬误材料，排除立场相反材料的干扰</span>
        </div>
      </div>
    </div>
  );
}

function BlockDetail({ stats }: { stats: BlockStats }) {
  return (
    <div className="rp-detail-inner">
      <div className="rp-detail-title">
        Section {stats.blockIndex + 1}
        <span className={`rp-detail-type-tag rp-detail-tag-${stats.blockType}`}>
          {blockTypeLabel(stats.blockType)}
        </span>
        <span className="rp-detail-n">n = {stats.n}</span>
      </div>
      <div className="rp-detail-grid">
        <StatRow label="认同均值" value={stats.meanAttitude} max={5} fillClass="rp-vfill-att" />
        <StatRow label="逻辑均值" value={stats.meanLogic} max={5} fillClass="rp-vfill-log" />
        {stats.meanFor !== null && (
          <StatRow label="FOR 均值" value={stats.meanFor} max={5} fillClass="rp-vfill-for" />
        )}
        <StatRow label="RT 均值" value={stats.meanRtS} max={15} fillClass="rp-swatch-rt" unit="s" />
      </div>
    </div>
  );
}

function GroupDetail({ blocks, kind }: { blocks: BlockStats[]; kind: "meta" | "non_meta" }) {
  const allAtt = avg(blocks.map((b) => b.meanAttitude));
  const allLog = avg(blocks.map((b) => b.meanLogic));
  const forBlocks = blocks.filter((b) => b.meanFor !== null);
  const allFor = forBlocks.length > 0 ? avg(forBlocks.map((b) => b.meanFor!)) : null;
  const allRt = avg(blocks.map((b) => b.meanRtS));
  const totalN = blocks.reduce((s, b) => s + b.n, 0);

  return (
    <div className="rp-detail-inner">
      <div className="rp-detail-title">
        <span className={`rp-detail-type-tag rp-detail-tag-${kind}`}>
          {blockTypeLabel(kind)}
        </span>
        <span className="rp-detail-subtitle">{blocks.length} 个 Section 的汇总</span>
        <span className="rp-detail-n">n = {totalN}</span>
      </div>
      <div className="rp-detail-grid">
        <StatRow label="认同均值" value={allAtt} max={5} fillClass="rp-vfill-att" />
        <StatRow label="逻辑均值" value={allLog} max={5} fillClass="rp-vfill-log" />
        {allFor !== null && (
          <StatRow label="FOR 均值" value={allFor} max={5} fillClass="rp-vfill-for" />
        )}
        <StatRow label="RT 均值" value={allRt} max={15} fillClass="rp-swatch-rt" unit="s" />
      </div>
    </div>
  );
}

function StatRow({
  label,
  value,
  max,
  fillClass,
  unit = "",
}: {
  label: string;
  value: number;
  max: number;
  fillClass: string;
  unit?: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="rp-stat-row">
      <span className="rp-stat-label">{label}</span>
      <div className="rp-stat-track">
        <div className={`rp-stat-fill ${fillClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="rp-stat-val">{value.toFixed(2)}{unit}</span>
    </div>
  );
}

function VBar({ value, fillClass, label }: { value: number; fillClass: string; label: string }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <div className="rp-vbar-wrap" title={`${label}: ${value.toFixed(2)}`}>
      <span className="rp-vbar-tip">{value.toFixed(1)}</span>
      <div className={`rp-vbar ${fillClass}`} style={{ height: `${pct}%` }} />
    </div>
  );
}
